import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { EditFieldType, LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { DetailPanelParams } from '../../Compounds/DetailPanel/DetailPanel';
import { getFormattedDate } from '../../../utils/helpers';
import { Adjustment, saveAdjustment, deleteAdjustmentTag } from '../../../store/Adjustment';
import { EntityType } from '../../../store/Search';
import { hasAnalystOrHigherPermissions, hasEngineerOrHigherPermissions } from '../../../store/User';
import { Icon } from '../../Atoms/Icon';
import { TextSpan } from '../../Atoms/Text';
import { NewAdjustmentTagDialog } from './NewAdjustmentTagDialog';
import { RunStatus } from '../../../store/Run';
import { ConfirmationModal } from '../../Atoms';

export interface AdjustmentDetailsParams {
  data: Adjustment;
}

const PLACEHOLDER = '–';

const toDisplay = (value: string | null | undefined) => value || PLACEHOLDER;

const toDisplayUser = (data: Adjustment['taggedBy'] | Adjustment['modifiedBy']) => {
  if (!data) return PLACEHOLDER;
  const name = `${data.firstName || ''} ${data.lastName || ''}`.trim();
  return name || data.email || PLACEHOLDER;
};

export const adjustmentSection = (data: Adjustment): DetailPanelParams[] => {
  const row = {
    ...data,
    runName: toDisplay(data.runName),
    rpmSupplierName: toDisplay(data.rpmSupplierName),
    agencyName: toDisplay(data.agencyName),
    adjustmentNote: toDisplay(data.adjustmentNote)
  };

  return [
    {
      data: row,
      canEdit: [],
      fieldMap: {
        runName: 'Run',
        runStatus: 'Run Status',
        rpmSupplierName: 'Supplier',
        agencyName: 'Agency',
        adjustmentType: 'Adjustment Type',
        grossComm: 'Gross Comm',
        salesComm: 'Sales Comm',
        netBilled: 'Net Billed',
        adjustmentNote: 'Adjustment Note'
      },
      order: [
        'runName',
        'runStatus',
        'rpmSupplierName',
        'agencyName',
        'adjustmentType',
        'grossComm',
        'salesComm',
        'netBilled',
        'adjustmentNote'
      ]
    }
  ];
};

export const tagSection = (data: Adjustment): DetailPanelParams[] => {
  const isTagged = !!data.adjustmentTagId;
  const canUpdate = isTagged && hasAnalystOrHigherPermissions();
  const row = {
    ...data,
    assetNumber: toDisplay(data.assetNumber),
    taggedByDisplay: toDisplayUser(data.taggedBy),
    taggedAtDisplay: toDisplay(data.taggedAt),
    note: toDisplay(data.note)
  };

  return [
    {
      data: row,
      canEdit: canUpdate
        ? [
            {
              field: 'assetUpstackId',
              type: EditFieldType.SEARCHBOX,
              mapType: EntityType.ASSET
            },
            {
              field: 'note',
              type: EditFieldType.TEXTAREA,
              useDefaultWidth: true
            }
          ]
        : [],
      fieldMap: {
        assetUpstackId: 'Asset',
        taggedByDisplay: 'Tagged By',
        taggedAtDisplay: 'Tagged At',
        note: 'Tag Note'
      },
      associationNameFields: {
        assetUpstackId: 'assetNumber'
      },
      order: ['assetUpstackId', 'taggedByDisplay', 'taggedAtDisplay', 'note']
    }
  ];
};

export const assetSection = (data: Adjustment): DetailPanelParams[] => {
  const isTagged = !!data.adjustmentTagId;
  const row = {
    ...data,
    assetNumber: toDisplay(data.assetNumber),
    customerName: toDisplay(data.customerName),
    supplierName: toDisplay(data.supplierName),
    commissionsStatus: toDisplay(data.commissionsStatus),
    salesAgentName: toDisplay(data.salesAgentName),
    tsdName: toDisplay(data.tsdName),
    actualFirstCommissionRun: toDisplay(data.actualFirstCommissionRun),
    actualDecommissionedDate: toDisplay(data.actualDecommissionedDate)
  };

  const links = isTagged
    ? [
        { field: 'assetNumber', link: `/asset/${data.assetUpstackId}`, external: false },
        { field: 'customerName', link: `/customer/${data.customerId}`, external: false }
      ]
    : [];

  return [
    {
      data: row,
      canEdit: [],
      fieldMap: {
        assetNumber: 'Asset',
        customerName: 'Customer',
        supplierName: 'Supplier',
        commissionsStatus: 'Commission Status',
        salesAgentName: 'Sales Agent',
        tsdName: 'TSD',
        actualFirstCommissionRun: 'First Commission Run',
        actualDecommissionedDate: 'Decommission Run'
      },
      order: [
        'assetNumber',
        'commissionsStatus',
        'customerName',
        'supplierName',
        'salesAgentName',
        'tsdName',
        'actualFirstCommissionRun',
        'actualDecommissionedDate'
      ],
      links
    }
  ];
};

export const AdjustmentDetails = ({ data }: AdjustmentDetailsParams) => {
  const isTagged = !!data.adjustmentTagId;
  const saveAction = (update: Adjustment) => saveAdjustment(update);
  const canModeByStatus =
    data.runStatus && ![RunStatus.LOCKED, RunStatus.PENDING_LOCK].includes(data.runStatus as RunStatus);
  const canAdd = hasAnalystOrHigherPermissions() && !isTagged && canModeByStatus;
  const canDelete = hasEngineerOrHigherPermissions() && isTagged && canModeByStatus;
  const [showNewTagDialog, setShowNewTagDialog] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);

  const handleAddClick = () => {
    if (!canAdd) return;
    setShowNewTagDialog(true);
  };

  const cardActions = () => {
    if (!canAdd) return undefined;
    return (
      <button
        className="flex items-center space-x-2"
        onClick={handleAddClick}
        data-cy="new-tag-button"
        data-testid="new-tag-button">
        <Icon type="plus" />
        <TextSpan
          size="sm14"
          color={canAdd ? 'indigo' : 'grey5'}>
          New Tag
        </TextSpan>
      </button>
    );
  };

  const handleRemove = async () => {
    await deleteAdjustmentTag(data);
  };

  const deleteAction = () => {
    if (!canDelete) return <></>;
    return (
      <div className="px-4 py-2">
        <div className="w-full border-t border-grey-2 my-3" />
        <button
          className="text-sm text-red rounded my-2 py-2 px-4 bg-[#FFEFEF] hover:bg-[#FDD3D3]"
          onClick={() => setOpenConfirmation(true)}>
          <DeleteIcon
            color="red"
            className="mr-2 -mt-1"
            fontSize="small"
          />
          <span>Delete Tag</span>
        </button>
        <ConfirmationModal
          open={openConfirmation}
          setOpen={setOpenConfirmation}
          handleSubmit={handleRemove}
          text="Are you sure you want to permanently delete this Adjustment Tag?"
          confirmButtonText="Delete"
        />
      </div>
    );
  };

  return (
    <div
      className="space-y-4"
      data-testid="adjustment-details-drawer">
      <DetailViewCard
        enableCollapse
        key="adjustment-section-card"
        title="Adjustment"
        cardActions={cardActions()}>
        <div className="min-h-[10rem]">
          <DetailPanelContainer data={adjustmentSection(data)} />
        </div>
      </DetailViewCard>

      <DetailViewCard
        enableCollapse
        key={`tag-section-card-${data.updatedAt}`}
        title="Tag">
        <div className="min-h-[5rem]">
          <DetailPanelContainer
            data={tagSection(data)}
            saveAction={(d) => saveAction(d as Adjustment)}
          />
        </div>
        {deleteAction()}
      </DetailViewCard>

      <DetailViewCard
        enableCollapse
        collapsed={!isTagged}
        key={`asset-section-card-${data.assetUpstackId}`}
        title="Asset">
        <div className="min-h-[10rem]">
          <DetailPanelContainer data={assetSection(data)} />
        </div>
      </DetailViewCard>

      <DetailViewCard
        enableCollapse
        collapsed={!isTagged}
        key={`meta-section-card-${data.updatedAt}`}
        title="Meta">
        <div className="flex px-4 pt-4 w-full">
          <LabelValueField
            label="Created At"
            value={data.createdAt ? getFormattedDate(data.createdAt, 'lll') : PLACEHOLDER}
            field="createdAt"
          />
          <LabelValueField
            label="Modified At"
            value={data.updatedAt ? getFormattedDate(data.updatedAt, 'lll') : PLACEHOLDER}
            field="updatedAt"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="Created By"
            value={toDisplayUser(data.taggedBy)}
            field="taggedBy.name"
          />
          <LabelValueField
            label="Modified By"
            value={toDisplayUser(data.modifiedBy)}
            field="modifiedBy.name"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="ID"
            value={toDisplay(data.adjustmentTagId)}
            field="adjustmentTagId"
          />
        </div>
      </DetailViewCard>

      <NewAdjustmentTagDialog
        open={showNewTagDialog}
        onClose={() => setShowNewTagDialog(false)}
        adjustment={data}
      />
    </div>
  );
};
